import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, Send, FileText, Loader2, Settings, Undo2, Search, Filter, BookOpen, Upload, History } from 'lucide-react';
import JSZip from 'jszip';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Card } from '@/shared/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useIsAdmin } from '@/shared/hooks/use-admin-validation';
import { BrainService } from '../services/brainService';
import { MindChangesDialog } from './MindChangesDialog';
import { WorkspaceOverviewService } from '../services/workspaceOverviewService';
import { WorkspaceOverviewBlock } from '../types/WorkspaceOverviewTypes';
import { OverviewBlockTree } from './OverviewBlockTree';
import { OverviewMarkdownEditor } from './OverviewMarkdownEditor';
import { OverviewSettingsDialog } from './OverviewSettingsDialog';
import { GenerateSectionDialog } from './GenerateSectionDialog';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';

export const KnowledgeOverview = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  // A Mind (AB#86817): todos leem; só administradores alteram, trancam e desfazem.
  const { isAdmin: canEdit } = useIsAdmin();
  const [changesOpen, setChangesOpen] = useState(false);

  const [blocks, setBlocks] = useState<WorkspaceOverviewBlock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [, setActiveBlockId] = useState<string | null>(null);
  const [editorSelectedBlockId, setEditorSelectedBlockId] = useState<string | null>(null);
  const [settingsMode, setSettingsMode] = useState<'settings' | 'generate' | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [newBlockId, setNewBlockId] = useState<string | null>(null);
  const [showGenerateSingleDialog, setShowGenerateSingleDialog] = useState(false);
  const [isGeneratingSingle, setIsGeneratingSingle] = useState(false);
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  const [dirtyBlockIds, setDirtyBlockIds] = useState<Set<string>>(new Set());
  const blockDraftCache = useRef<Map<string, Partial<WorkspaceOverviewBlock>>>(new Map());
  const originalBlocksRef = useRef<Map<string, WorkspaceOverviewBlock>>(new Map());
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const editorSelectedBlock = useMemo(() => {
    const block = blocks.find(b => b.id === editorSelectedBlockId) ?? null;
    if (!block) return null;
    const draft = blockDraftCache.current.get(block.id);
    return draft ? { ...block, ...draft } as WorkspaceOverviewBlock : block;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, editorSelectedBlockId, dirtyBlockIds]);

  const blockTypeColumns = useMemo(() =>
    [...new Set(blocks.map(b => b.block_type))].map(type => ({
      id: type,
      name: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      type: 'text' as const,
    }))
  , [blocks]);

  const filteredBlocks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return blocks.filter(block => {
      if (selectedTypes.size > 0 && !selectedTypes.has(block.block_type)) return false;
      if (!q) return true;
      if (block.title?.toLowerCase().includes(q)) return true;
      if (block.description?.toLowerCase().includes(q)) return true;
      if (block.content?.value?.toLowerCase().includes(q)) return true;
      if (Array.isArray(block.content?.items)) {
        return block.content.items.some(item =>
          typeof item === 'string'
            ? item.toLowerCase().includes(q)
            : (item.name?.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q))
        );
      }
      return false;
    });
  }, [blocks, searchQuery, selectedTypes]);

  const loadBlocks = useCallback(async (): Promise<WorkspaceOverviewBlock[] | undefined> => {
    if (!currentWorkspace?.id) return;

    try {
      setIsLoading(true);
      const data = await WorkspaceOverviewService.getBlocksByWorkspaceId(currentWorkspace.id);
      setBlocks(data);
      setPendingDeletes(new Set());
      setDirtyBlockIds(new Set());
      blockDraftCache.current.clear();
      originalBlocksRef.current = new Map(data.map(b => [b.id, b]));
      return data;
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('knowledge.overview.error_loading'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace?.id, toast, t]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadBlocks();
    }
  }, [currentWorkspace?.id, loadBlocks]);

  // Auto-select first block when blocks load
  useEffect(() => {
    if (blocks.length > 0 && !editorSelectedBlockId) {
      setEditorSelectedBlockId(blocks[0].id);
    }
  }, [blocks, editorSelectedBlockId]);

  const handleAddBlock = () => {
    if (!currentWorkspace?.id) return;
    const existingNumbers = blocks
      .map(b => b.field_name.match(/^section_(\d+)$/))
      .filter(Boolean)
      .map(m => Number(m![1]));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const fieldName = `section_${nextNumber}`;

    const newBlock: WorkspaceOverviewBlock = {
      id: crypto.randomUUID(),
      product_id: currentWorkspace.id,
      field_name: fieldName,
      title: t('knowledge.overview.new_section_title'),
      description: t('knowledge.overview.new_section_description'),
      block_type: 'textarea',
      content: {},
      order_index: blocks.length,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setBlocks(prev => [...prev, newBlock]);
    setEditorSelectedBlockId(newBlock.id);
    setNewBlockId(newBlock.id);
    setDirtyBlockIds(prev => new Set(prev).add(newBlock.id));
  };

  // maxTokens/temperature vem do prop `onGenerate` mas nao sao usados: o endpoint
  // /overview/generate-content usa os limites do backend.
  const handleGenerateSingleBlock = useCallback(async (prompt: string) => {
    if (!currentWorkspace?.id || !prompt.trim()) return;
    setIsGeneratingSingle(true);
    try {
      const titleMatch = prompt.match(/^(?:Section title:|Título da seção:)\s*(.+)/im);
      const firstLine = prompt.trim().split('\n')[0].replace(/^#+\s*/, '').trim();
      const rawTitle = titleMatch?.[1]?.trim() || firstLine || 'New Section';
      const title = rawTitle.replace(/<[^>]+>/g, '').trim() || 'New Section';

      const result = await WorkspaceOverviewService.generateBlockContent(
        currentWorkspace.id,
        'textarea',
        title,
        prompt.trim(),
        i18n.language,
      );
      const fieldName = title.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `section_${blocks.length + 1}`;
      const now = new Date().toISOString();
      const draft: WorkspaceOverviewBlock = {
        id: crypto.randomUUID(),
        product_id: currentWorkspace.id,
        field_name: fieldName,
        title,
        description: '',
        block_type: 'textarea',
        content: result.content,
        order_index: blocks.length,
        is_active: true,
        created_at: now,
        updated_at: now,
      };
      setBlocks(prev => [...prev, draft]);
      setDirtyBlockIds(prev => new Set(prev).add(draft.id));
      setEditorSelectedBlockId(draft.id);
      setNewBlockId(draft.id);
    } catch (error) {
      toast({ title: t('common.error'), description: resolveApiErrorMessage(error, t), variant: 'destructive' });
    } finally {
      setIsGeneratingSingle(false);
    }
  }, [currentWorkspace, blocks.length, i18n.language, toast, t]);

  const parseMarkdownToBlocks = useCallback((filename: string, content: string): Omit<WorkspaceOverviewBlock, 'id' | 'created_at' | 'updated_at'>[] => {
    if (!currentWorkspace?.id) return [];
    const title = filename.replace(/\.md$/i, '');
    const fieldName = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const startIndex = blocks.length;
    return [{
      product_id: currentWorkspace.id,
      field_name: fieldName || `section_${startIndex + 1}`,
      title,
      description: '',
      block_type: 'textarea' as const,
      content: { value: content.trim() },
      order_index: startIndex,
      is_active: true,
    }];
  }, [currentWorkspace, blocks.length]);

  const processFile = useCallback(async (file: File) => {
    if (!currentWorkspace?.id) return;

    setIsUploading(true);
    try {
      let parsedBlocks: Omit<WorkspaceOverviewBlock, 'id' | 'created_at' | 'updated_at'>[] = [];

      if (file.name.endsWith('.md')) {
        const text = await file.text();
        parsedBlocks = parseMarkdownToBlocks(file.name, text);
      } else if (file.name.endsWith('.zip')) {
        const zip = await JSZip.loadAsync(await file.arrayBuffer());
        const mdFiles = Object.values(zip.files).filter(f => !f.dir && f.name.endsWith('.md'));
        for (const mdFile of mdFiles) {
          const text = await mdFile.async('text');
          parsedBlocks = [...parsedBlocks, ...parseMarkdownToBlocks(mdFile.name.split('/').pop() ?? mdFile.name, text)];
        }
      } else {
        toast({ title: t('knowledge.overview.upload_invalid_type'), variant: 'destructive' });
        return;
      }

      if (parsedBlocks.length === 0) {
        toast({ title: t('knowledge.overview.upload_empty'), variant: 'destructive' });
        return;
      }

      const now = new Date().toISOString();
      const draftBlocks: WorkspaceOverviewBlock[] = parsedBlocks.map(b => ({
        ...b,
        id: crypto.randomUUID(),
        created_at: now,
        updated_at: now,
      }));

      setBlocks(prev => [...prev, ...draftBlocks]);
      setDirtyBlockIds(prev => {
        const next = new Set(prev);
        draftBlocks.forEach(b => next.add(b.id));
        return next;
      });
      setEditorSelectedBlockId(draftBlocks[0].id);
      setNewBlockId(draftBlocks[0].id);

      toast({ title: t('knowledge.overview.upload_success', { count: draftBlocks.length }) });
    } catch (error) {
      toast({
        title: t('knowledge.overview.upload_error'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  }, [currentWorkspace, parseMarkdownToBlocks, toast, t]);

  const handleUploadFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadInputRef.current) uploadInputRef.current.value = '';
    await processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const hasFile = Array.from(e.dataTransfer.items).some(i => i.kind === 'file');
    if (hasFile) setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  }, [processFile]);

  const handleDeleteBlock = (blockId: string) => {
    // Bloco trancado não se exclui pela tela (AB#86817), venha de onde vier o pedido.
    if (blocks.find(b => b.id === blockId)?.locked) return;
    setPendingDeletes(prev => new Set(prev).add(blockId));
    const newBlocks = blocks.filter(b => b.id !== blockId);
    setBlocks(newBlocks);

    if (editorSelectedBlockId === blockId) {
      const idx = blocks.findIndex(b => b.id === blockId);
      const next = newBlocks[idx] ?? newBlocks[idx - 1] ?? null;
      setEditorSelectedBlockId(next?.id ?? null);
    }
  };

  const handleBlockUpdate = useCallback((blockId: string, updates: Partial<WorkspaceOverviewBlock>) => {
    // Sync cache — instantly readable by new editor mounts
    const existing = blockDraftCache.current.get(blockId) || {};
    const merged = { ...existing, ...updates };
    blockDraftCache.current.set(blockId, merged);

    // Async state update
    setBlocks(prev => {
      const updated = prev.map(b => (b.id === blockId ? { ...b, ...updates } : b));

      // Check if block returned to original state
      const original = originalBlocksRef.current.get(blockId);
      const current = updated.find(b => b.id === blockId);
      if (original && current) {
        const isContentSame = JSON.stringify(current.content) === JSON.stringify(original.content);
        const isMetaSame = current.title === original.title
          && current.description === original.description
          && current.block_type === original.block_type
          && current.field_name === original.field_name;

        if (isContentSame && isMetaSame) {
          blockDraftCache.current.delete(blockId);
          setDirtyBlockIds(prev => {
            const next = new Set(prev);
            next.delete(blockId);
            return next;
          });
        } else {
          setDirtyBlockIds(prev => new Set(prev).add(blockId));
        }
      } else {
        setDirtyBlockIds(prev => new Set(prev).add(blockId));
      }

      return updated;
    });
  }, []);

  const hasPendingChanges = pendingDeletes.size > 0 || dirtyBlockIds.size > 0;
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const allowNavigationRef = useRef(false);

  // Browser beforeunload warning
  useEffect(() => {
    if (!hasPendingChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasPendingChanges]);

  // Intercept in-app navigation (click capture)
  useEffect(() => {
    if (!hasPendingChanges) return;

    const handleClick = (e: MouseEvent) => {
      if (allowNavigationRef.current) return;

      const target = (e.target as HTMLElement).closest('a[href], [data-href]');
      if (!target) return;

      const href = target.getAttribute('href') || target.getAttribute('data-href');
      if (!href || href === '#' || href.startsWith('#')) return;

      e.preventDefault();
      e.stopPropagation();
      pendingNavigationRef.current = href;
      setShowUnsavedDialog(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasPendingChanges]);

  const handleDiscardChanges = () => {
    allowNavigationRef.current = true;
    setShowUnsavedDialog(false);
    setPendingDeletes(new Set());
    setDirtyBlockIds(new Set());

    if (pendingNavigationRef.current) {
      const target = pendingNavigationRef.current;
      if (target.startsWith('http') || target.startsWith('//')) {
        window.location.href = target;
      } else {
        navigate(target);
      }
    }
  };

  const handleContinueEditing = () => {
    setShowUnsavedDialog(false);
    pendingNavigationRef.current = null;
  };

  // O check da edição grava só este bloco; o Publish continua gravando todos os
  // pendentes de uma vez e sincronizando a Knowledge.
  const handleSaveBlock = async (blockId: string): Promise<boolean> => {
    const current = blocks.find(b => b.id === blockId);
    if (!current || !currentWorkspace?.id) return false;
    if (!dirtyBlockIds.has(blockId)) return true;
    const block = { ...current, ...blockDraftCache.current.get(blockId) };
    try {
      if (originalBlocksRef.current.has(blockId)) {
        await WorkspaceOverviewService.updateBlock(blockId, {
          field_name: block.field_name,
          title: block.title,
          description: block.description,
          block_type: block.block_type,
          content: block.content,
        });
        originalBlocksRef.current.set(blockId, block);
        blockDraftCache.current.delete(blockId);
        setDirtyBlockIds(prev => {
          const next = new Set(prev);
          next.delete(blockId);
          return next;
        });
      } else {
        await WorkspaceOverviewService.createBlock({
          product_id: block.product_id,
          field_name: block.field_name,
          title: block.title,
          description: block.description,
          block_type: block.block_type,
          content: block.content,
          order_index: block.order_index,
          is_active: block.is_active,
        });
        // O bloco novo ganha o id do banco: recarrega e mantém a seleção nele.
        blockDraftCache.current.delete(blockId);
        setDirtyBlockIds(prev => {
          const next = new Set(prev);
          next.delete(blockId);
          return next;
        });
        const reloaded = await loadBlocks();
        const created = reloaded?.find(b => b.field_name === block.field_name);
        if (created) setEditorSelectedBlockId(created.id);
      }
      toast({ title: t('common.success'), description: t('knowledge.mind.blockSaved') });
      return true;
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('knowledge.mind.blockSaveError'),
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleSyncToKnowledgeBase = async () => {
    if (!currentWorkspace?.id) return;

    setIsSyncing(true);

    const safetyTimeout = setTimeout(() => {
      setIsSyncing(false);
    }, 30000);

    try {
      // Exclusões pendentes (só dos blocos que existem no banco), em paralelo. Um bloco
      // que já tinha saído (404) conta como excluído; qualquer outra falha interrompe.
      const deletes = await Promise.allSettled(
        [...pendingDeletes]
          .filter(blockId => originalBlocksRef.current.has(blockId))
          .map(blockId => WorkspaceOverviewService.deleteBlock(blockId)),
      );
      const deleteFailure = deletes.find(
        (r): r is PromiseRejectedResult =>
          r.status === 'rejected' && (r.reason as { status?: number })?.status !== 404,
      );
      if (deleteFailure) throw deleteFailure.reason;

      // Blocos alterados: cria os novos e atualiza os existentes, em paralelo. Um trancado
      // nunca é publicado pela tela (AB#86817), mesmo que tenha ficado pendente.
      const writes = await Promise.allSettled(
        [...dirtyBlockIds]
          .map(blockId => blocks.find(b => b.id === blockId))
          .filter((block): block is WorkspaceOverviewBlock =>
            !!block && !pendingDeletes.has(block.id) && !block.locked)
          .map(block =>
            originalBlocksRef.current.has(block.id)
              ? WorkspaceOverviewService.updateBlock(block.id, {
                  field_name: block.field_name,
                  title: block.title,
                  description: block.description,
                  block_type: block.block_type,
                  content: block.content,
                })
              : WorkspaceOverviewService.createBlock({
                  product_id: block.product_id,
                  field_name: block.field_name,
                  title: block.title,
                  description: block.description,
                  block_type: block.block_type,
                  content: block.content,
                  order_index: block.order_index,
                  is_active: block.is_active,
                }),
          ),
      );
      const writeFailure = writes.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (writeFailure) throw writeFailure.reason;

      setPendingDeletes(new Set());
      setDirtyBlockIds(new Set());
      blockDraftCache.current.clear();

      // Reload from DB to get real IDs for newly created blocks
      const reloaded = await loadBlocks();
      if (reloaded && reloaded.length > 0) {
        setEditorSelectedBlockId(reloaded[0].id);
      }

      const result = await WorkspaceOverviewService.syncAllBlocksToKnowledgeBase(currentWorkspace.id);

      clearTimeout(safetyTimeout);

      if (result.success) {
        toast({
          title: t('common.success'),
          description: t('knowledge.overview.publish_success'),
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.message || t('knowledge.overview.error_syncing_to_kb'),
          variant: 'destructive',
        });
      }
    } catch (error: unknown) {
      toast({
        title: t('common.error'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      clearTimeout(safetyTimeout);
      setIsSyncing(false);
    }
  };

  const handleClearAll = async () => {
    if (!currentWorkspace?.id) return;
    // Os trancados ficam (AB#86817): limpar tudo apaga só os destrancados.
    const removable = blocks.filter(block => !block.locked);
    const results = await Promise.allSettled(
      removable.map(block => WorkspaceOverviewService.deleteBlock(block.id)),
    );
    // O que não saiu do banco continua na tela, para a pessoa ver e tentar de novo.
    const failedIds = new Set(
      removable.filter((_, i) => results[i].status === 'rejected').map(block => block.id),
    );
    const kept = blocks.filter(block => block.locked || failedIds.has(block.id));

    setBlocks(kept);
    setEditorSelectedBlockId(kept[0]?.id ?? null);
    setPendingDeletes(new Set());
    setDirtyBlockIds(new Set());
    blockDraftCache.current.clear();
    for (const id of [...originalBlocksRef.current.keys()]) {
      if (!kept.some(block => block.id === id)) originalBlocksRef.current.delete(id);
    }

    if (failedIds.size > 0) {
      toast({
        title: t('common.error'),
        description: t('knowledge.mind.clearPartial', { count: failedIds.size }),
        variant: 'destructive',
      });
    }

    try {
      // A limpeza das embeddings é do workspace inteiro: os blocos que ficaram são
      // reindexados logo depois, senão somem da busca.
      await WorkspaceOverviewService.clearOverviewEmbeddings(currentWorkspace.id);
      if (kept.length > 0) {
        await WorkspaceOverviewService.syncAllBlocksToKnowledgeBase(currentWorkspace.id);
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
      return;
    }

    if (failedIds.size > 0) return;
    toast({
      title: t('common.success'),
      description: kept.length > 0
        ? t('knowledge.mind.lockedKept', { count: kept.length })
        : t('knowledge.overview.all_blocks_cleared'),
    });
  };

  const handleGenerateOrRecreate = async (customPrompt?: string, maxTokens?: number, temperature?: number) => {
    setIsGenerating(true);
    setEditorSelectedBlockId(null);

    try {
      if (!currentWorkspace?.id) return;

      // Generate in preview mode — blocks are NOT saved to DB
      const generatedBlocks = await WorkspaceOverviewService.generateBlocks(currentWorkspace.id, i18n.language, true, customPrompt, maxTokens, temperature);

      // Os trancados ficam como estão (AB#86817); só os destrancados dão lugar aos gerados,
      // e um gerado com a chave de um trancado é descartado.
      const locked = blocks.filter(b => b.locked);
      const lockedKeys = new Set(locked.map(b => b.field_name));
      const fresh = generatedBlocks.filter(b => !lockedKeys.has(b.field_name));

      // Mark existing DB blocks for deletion on publish (don't delete from DB yet — preserves undo)
      const existingDbIds = blocks
        .filter(b => !b.locked && originalBlocksRef.current.has(b.id))
        .map(b => b.id);
      setPendingDeletes(new Set(existingDbIds));

      // Set generated blocks as local draft state
      setBlocks([...locked, ...fresh]);
      blockDraftCache.current.clear();
      // originalBlocksRef intentionally preserved: needed for pendingDeletes logic and undo (Undo2 button calls loadBlocks())

      // Mark ALL generated blocks as dirty (need publish to persist)
      setDirtyBlockIds(new Set(fresh.map(b => b.id)));

      // Select first block and open in edit mode
      if (fresh.length > 0) {
        setEditorSelectedBlockId(fresh[0].id);
        setNewBlockId(fresh[0].id);
      }

    } catch (error) {
      toast({
        title: t('common.error'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveBlockId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveBlockId(null);
      return;
    }

    const oldIndex = blocks.findIndex(b => b.id === active.id);
    const newIndex = blocks.findIndex(b => b.id === over.id);

    const newBlocks = arrayMove(blocks, oldIndex, newIndex);
    setBlocks(newBlocks);

    // Only persist reorder if all blocks are already saved in DB
    const hasUnpersistedBlocks = newBlocks.some(b => !originalBlocksRef.current.has(b.id));
    if (!hasUnpersistedBlocks && currentWorkspace?.id) {
      const reorderData = {
        blocks: newBlocks.map((block, index) => ({
          id: block.id,
          order_index: index,
        })),
      };

      try {
        await WorkspaceOverviewService.reorderBlocks(currentWorkspace.id, reorderData);
      } catch (error) {
        setBlocks(blocks);
        toast({
          title: t('common.error'),
          description: resolveApiErrorMessage(error, t),
          variant: 'destructive',
        });
      }
    } else {
      // Draft mode — mark as dirty, order will be persisted on publish
      setDirtyBlockIds(prev => {
        const next = new Set(prev);
        // Reordenar não altera o conteúdo: um bloco trancado não vira pendência.
        newBlocks.filter(b => !b.locked).forEach(b => next.add(b.id));
        return next;
      });
    }

    setActiveBlockId(null);
  };

  const handleDragCancel = () => {
    setActiveBlockId(null);
  };

  const handleToggleLock = async (block: WorkspaceOverviewBlock) => {
    if (!currentWorkspace?.id || !canEdit) return;
    const locked = !block.locked;
    // Trancar um bloco com alteração pendente deixaria o Publish gravar por cima do cadeado.
    if (locked && dirtyBlockIds.has(block.id)) {
      toast({ title: t('knowledge.mind.lockError'), description: t('knowledge.mind.lockDirty'), variant: 'destructive' });
      return;
    }
    try {
      await BrainService.setBlockLock(currentWorkspace.id, block.id, locked);
      setBlocks(prev => prev.map(b => (b.id === block.id ? { ...b, locked } : b)));
      const original = originalBlocksRef.current.get(block.id);
      if (original) originalBlocksRef.current.set(block.id, { ...original, locked });
    } catch (error) {
      toast({
        title: t('knowledge.mind.lockError'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('knowledge.overview.no_workspace')}</p>
      </div>
    );
  }

  return (
    <div
      className="space-y-4 relative"
      onDragOver={canEdit ? handleDragOver : undefined}
      onDragLeave={canEdit ? handleDragLeave : undefined}
      onDrop={canEdit ? handleDrop : undefined}
    >
      <input
        ref={uploadInputRef}
        type="file"
        accept=".md,.zip"
        className="hidden"
        onChange={handleUploadFile}
      />
      {isDragOver && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 pointer-events-none">
          <Upload className="w-10 h-10 text-primary mb-2" />
          <p className="text-sm font-medium text-primary">{t('knowledge.overview.drop_to_import', 'Drop to import')}</p>
          <p className="text-xs text-muted-foreground mt-1">.md or .zip</p>
        </div>
      )}
      {(isGenerating || isSyncing) && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <LoadingSpinner size="md" />
            <p className="text-sm text-muted-foreground -mt-16">
              {isGenerating ? t('knowledge.overview.generating') : t('common.syncing')}
            </p>
          </div>
        </div>
      )}

      <Card>
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                <Input
                  className="pl-8 w-[250px]"
                  placeholder={t('knowledge.overview.search_placeholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <div className="relative">
                    <Button variant="outline" size="icon">
                      <Filter className="h-4 w-4" />
                    </Button>
                    {selectedTypes.size > 0 && (
                      <Badge
                        variant="secondary"
                        className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center"
                      >
                        {selectedTypes.size}
                      </Badge>
                    )}
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <div className="flex items-center justify-between border-b px-3 py-2">
                    <h3 className="font-medium">{t('common.actions.filter', 'Filter by')}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTypes(new Set())}>
                      {t('common.actions.reset', 'Reset')}
                    </Button>
                  </div>
                  <div className="p-4 space-y-2">
                    {blockTypeColumns.map(col => (
                      <div key={col.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`type-${col.id}`}
                          checked={selectedTypes.has(col.id)}
                          onCheckedChange={checked => {
                            setSelectedTypes(prev => {
                              const next = new Set(prev);
                              checked ? next.add(col.id) : next.delete(col.id);
                              return next;
                            });
                          }}
                        />
                        <Label htmlFor={`type-${col.id}`} className="text-sm font-normal cursor-pointer">
                          {col.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              {canEdit && (<>
              {hasPendingChanges && (
                <Badge variant="outline" className="text-xs font-normal border-yellow-400 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950/30">{t('knowledge.overview.pending_changes_short')}</Badge>
              )}
              {hasPendingChanges && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => loadBlocks()}
                >
                  <Undo2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                onClick={handleSyncToKnowledgeBase}
                disabled={isSyncing || !hasPendingChanges || isGenerating}
                variant={hasPendingChanges ? 'default' : 'outline'}
              >
                <Send className="w-4 h-4 mr-2" />
                {isSyncing ? t('common.syncing') : t('knowledge.overview.publish')}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSettingsMode('generate')}
                disabled={isGenerating}
                title={t('knowledge.overview.settings_generate_title')}
              >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
              </>)}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setChangesOpen(true)}
                title={t('knowledge.mind.changes')}
                aria-label={t('knowledge.mind.changes')}
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSettingsMode('settings')}
                title={t('knowledge.overview.settings_title')}
                aria-label={t('knowledge.overview.settings_title')}
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Main content */}
          {isLoading && !isSyncing && !isGenerating ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : blocks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-280px)] border-2 border-dashed rounded-lg gap-8">
              <div className="flex flex-col items-center gap-2">
                <BookOpen className="w-24 h-24 text-muted-foreground/40" />
                <p className="text-muted-foreground text-sm max-w-md text-center">{t('knowledge.overview.no_blocks')}</p>
              </div>
              {canEdit && (
              <div className="flex gap-4">
                <button
                  onClick={handleAddBlock}
                  className="flex flex-col items-center gap-3 p-6 w-44 rounded-xl border-2 border-dashed hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10">
                    <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">{t('knowledge.overview.add')}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('knowledge.overview.add_description')}</p>
                  </div>
                </button>

                <button
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex flex-col items-center gap-3 p-6 w-44 rounded-xl border-2 border-dashed hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10">
                    {isUploading ? <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" /> : <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary" />}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">{t('knowledge.overview.upload')}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('knowledge.overview.upload_description')}</p>
                  </div>
                </button>

                <button
                  onClick={() => setSettingsMode('generate')}
                  disabled={isGenerating}
                  className="flex flex-col items-center gap-3 p-6 w-44 rounded-xl border-2 border-dashed hover:border-[#0B6FC2] hover:bg-[#0B6FC2]/5 transition-colors cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-[#0B6FC2]/10">
                    {isGenerating ? <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" /> : <Sparkles className="w-5 h-5 text-muted-foreground group-hover:text-[#0B6FC2]" />}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">{t('knowledge.overview.generate_blocks')}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('knowledge.overview.generate_description')}</p>
                  </div>
                </button>
              </div>
              )}
            </div>
          ) : filteredBlocks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">{t('knowledge.overview.no_search_results')}</p>
            </div>
          ) : (
            <div className="flex gap-0 border rounded-lg overflow-hidden h-[calc(100vh-280px)]">
              <OverviewBlockTree
                blocks={filteredBlocks}
                selectedBlockId={editorSelectedBlockId}
                dirtyBlockIds={dirtyBlockIds}
                canEdit={canEdit}
                onSelect={block => setEditorSelectedBlockId(block.id)}
                onDelete={handleDeleteBlock}
                onToggleLock={handleToggleLock}
                onAdd={handleAddBlock}
                onUpload={() => uploadInputRef.current?.click()}
                onGenerate={() => setShowGenerateSingleDialog(true)}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
              />
              <div className="flex-1 border-l overflow-hidden">
                {editorSelectedBlock ? (
                  <OverviewMarkdownEditor
                    key={editorSelectedBlock.id}
                    block={editorSelectedBlock}
                    workspaceId={currentWorkspace.id}
                    initialEditing={editorSelectedBlock.id === newBlockId}
                    canEdit={canEdit}
                    onToggleLock={() => handleToggleLock(editorSelectedBlock)}
                    onDelete={handleDeleteBlock}
                    onBlockUpdate={handleBlockUpdate}
                    onSave={() => handleSaveBlock(editorSelectedBlock.id)}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                    <FileText className="w-8 h-8 opacity-40" />
                    <p className="text-sm">{t('knowledge.mind.selectBlock')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {!canEdit && <p className="text-xs text-muted-foreground">{t('knowledge.mind.readOnlyNote')}</p>}
        </div>
      </Card>

      <MindChangesDialog
        workspaceId={currentWorkspace.id}
        open={changesOpen}
        onOpenChange={setChangesOpen}
        canUndo={canEdit}
        onUndone={() => loadBlocks()}
      />

      <GenerateSectionDialog
        open={showGenerateSingleDialog}
        onOpenChange={setShowGenerateSingleDialog}
        onGenerate={handleGenerateSingleBlock}
        isGenerating={isGeneratingSingle}
      />

      <OverviewSettingsDialog
        open={settingsMode !== null}
        onOpenChange={(open) => { if (!open) setSettingsMode(null); }}
        onClearAll={handleClearAll}
        onRegenerateAll={handleGenerateOrRecreate}
        isGenerating={isGenerating}
        mode={settingsMode || 'settings'}
        workspaceId={currentWorkspace.id}
        canEdit={canEdit}
      />

      <Dialog open={showUnsavedDialog} onOpenChange={(open) => { if (!open) handleContinueEditing(); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('knowledge.overview.unsaved_changes_title')}</DialogTitle>
            <DialogDescription>{t('knowledge.overview.unsaved_changes_description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleContinueEditing}>
              {t('knowledge.overview.unsaved_changes_continue')}
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges}>
              {t('knowledge.overview.unsaved_changes_discard')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
